// src/components/chat/ChatNotificationListener.tsx
// Global listener for chat notifications - runs on all pages
import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';
import { chatService } from '../../services/chat.service';
import { ChatRoom } from '../../types/chat.types';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const ChatNotificationListener: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { setUnreadCount } = useChatNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const lastMessageTimestampRef = useRef<Record<string, number>>({});
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if (!currentUser || !userProfile) return;

    // Subscribe to all rooms for this user
    const unsubscribe = chatService.subscribeToUserRooms(
      currentUser.uid,
      userProfile.role,
      userProfile.projects || [],
      (rooms: ChatRoom[]) => {
        let totalUnread = 0;

        rooms.forEach(room => {
          if (room.lastMessage && room.lastMessageAt) {
            const messageTime = room.lastMessageAt.toDate().getTime();
            const lastSeen = lastMessageTimestampRef.current[room.id] || 0;

            // Check if this is a new message from someone else
            if (messageTime > lastSeen && room.lastMessageSenderId !== currentUser.uid) {
              totalUnread++;

              // Only show toast if:
              // 1. Not the initial load
              // 2. Message is very recent (within 10 seconds)
              // 3. User is NOT on the chat page (they'll see it there)
              const isOnChatPage = location.pathname === '/chat';
              const isRecent = messageTime > Date.now() - 10000;

              if (!initialLoadRef.current && isRecent && !isOnChatPage) {
                // Show notification toast
                toast.custom((t) => (
                  <div
                    className={`${t.visible ? 'animate-enter' : 'animate-leave'}
                      max-w-md w-full bg-white shadow-lg rounded-xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 cursor-pointer`}
                    onClick={() => {
                      navigate('/chat');
                      toast.dismiss(t.id);
                    }}
                  >
                    <div className="flex-1 w-0 p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                            {room.lastMessageSenderName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                          </div>
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {room.lastMessageSenderName || 'Unknown'} <span className="text-gray-500 font-normal">in #{room.name}</span>
                          </p>
                          <p className="mt-1 text-sm text-gray-500 truncate">
                            {room.lastMessage}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex border-l border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.dismiss(t.id);
                        }}
                        className="w-full border border-transparent rounded-none rounded-r-xl p-4 flex items-center justify-center text-sm font-medium text-violet-600 hover:text-violet-500 focus:outline-none"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ), { duration: 5000 });
              }
            }

            // Update last seen timestamp
            lastMessageTimestampRef.current[room.id] = messageTime;
          }
        });

        // Update global unread count
        setUnreadCount(totalUnread);

        // Mark initial load as complete after first update
        if (initialLoadRef.current) {
          initialLoadRef.current = false;
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser, userProfile, setUnreadCount, navigate, location.pathname]);

  // This component doesn't render anything
  return null;
};

export default ChatNotificationListener;
